import React, { useEffect, useState, useMemo, useContext } from "react";
import { useParams } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { custDb } from "../../firebaseCust";
import ProductFilters from "./FilteredProducts";
import { CartContext } from "../../CartContext"; // adjust path if needed
import "../../storeFront.css";

export default function StoreFront() {
  const { storeCode } = useParams();
  const { addOneToCart } = useContext(CartContext);

  const [products, setProducts] = useState([]);
  const [storeInfo, setStoreInfo] = useState(null);
  const [selectedImages, setSelectedImages] = useState({});
  const [activeTags, setActiveTags] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState({}); // { productId: {size, color} }

  // LOAD STORE & PRODUCTS
  useEffect(() => {
    const loadStore = async () => {
      try {
        const productsRef = collection(custDb, "customers", storeCode, "products");
        const snapshot = await getDocs(productsRef);

        const data = snapshot.docs.map((doc) => {
          const product = doc.data();
          const images = [product.image1, product.image2, product.image3].filter(Boolean);
          return {
            id: doc.id,
            ...product,
            images,
            tags: product.tags
              ? Array.isArray(product.tags)
                ? product.tags
                : product.tags.split(",").map((t) => t.trim())
              : [],
            sizes: product.size
              ? Array.isArray(product.size)
                ? product.size
                : product.size.split(",").map((s) => s.trim())
              : [],
            colors: product.color
              ? Array.isArray(product.color)
                ? product.color
                : product.color.split(",").map((c) => c.trim())
              : [],
          };
        });

        setProducts(data);

        // INITIAL MAIN IMAGES
        const firstImages = {};
        data.forEach((p) => (firstImages[p.id] = p.images[0] || ""));
        setSelectedImages(firstImages);

        // STORE INFO
        const infoRef = collection(custDb, "customers", storeCode, "details");
        const infoSnap = await getDocs(infoRef);
        if (!infoSnap.empty) setStoreInfo(infoSnap.docs[0].data());

      } catch (error) {
        console.error("Error loading store:", error);
      }
    };

    loadStore();
  }, [storeCode]);

  // FILTER PRODUCTS
  const filteredProducts = useMemo(() => {
    if (activeTags.length === 0) return products;
    return products.filter((p) => activeTags.every((tag) => p.tags?.includes(tag)));
  }, [products, activeTags]);

  // HANDLE SIZE/COLOR SELECTION
  const handleOptionChange = (productId, type, value) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [productId]: { ...prev[productId], [type]: value },
    }));
  };

  return (
    <div className="store-container">
      {/* STORE HEADER */}
      {storeInfo && (
        <div className="store-header">
          {storeInfo.bannerUrl && <img src={storeInfo.bannerUrl} alt="banner" className="store-banner" />}
          {storeInfo.logoUrl && <img src={storeInfo.logoUrl} alt="logo" className="store-logo" />}
          <h1 className="store-title" style={{ color: storeInfo.primaryColor || "#000" }}>
            {storeInfo.storeName || storeCode}
          </h1>
          <p className="store-description">{storeInfo.description}</p>
        </div>
      )}

      {/* FILTERS */}
      <div className="filter-section">
        <ProductFilters products={products} onChange={setActiveTags} />
      </div>

      {/* PRODUCTS GRID */}
      <div className="products-grid">
        {filteredProducts.length === 0 && <p className="no-products">No products found for selected filters</p>}

        {filteredProducts.map((product) => (
          <div key={product.id} className="product-card">

            {/* IMAGE + THUMBNAILS */}
            <div className="product-media">
              <div className="product-image-wrapper">
                <img
                  src={selectedImages[product.id] || product.images[0]}
                  alt={product.productName}
                  className="product-main-image"
                />
              </div>
              {product.images.length > 1 && (
                <div className="product-thumbnails">
                  {product.images.map((img, i) => (
                    <img
                      key={i}
                      src={img}
                      alt="thumb"
                      onClick={() =>
                        setSelectedImages((prev) => ({ ...prev, [product.id]: img }))
                      }
                      className="thumbnail-image"
                    />
                  ))}
                </div>
              )}
            </div>

            <h2 className="product-name">{product.productName}</h2>
            <p className="product-description">{product.description}</p>
            <p className="product-price">${product.price}</p>

            {/* SIZE & COLOR SELECT */}
            <div className="select-container">
              {product.sizes.length > 0 ? (
                <select
                  className="product-select"
                  onChange={(e) => handleOptionChange(product.id, "size", e.target.value)}
                  value={selectedOptions[product.id]?.size || ""}
                >
                  <option value="" disabled>
                    Select size
                  </option>
                  {product.sizes.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              ) : <div className="select-placeholder"></div>}

              {product.colors.length > 0 ? (
                <select
                  className="product-select"
                  onChange={(e) => handleOptionChange(product.id, "color", e.target.value)}
                  value={selectedOptions[product.id]?.color || ""}
                >
                  <option value="" disabled>
                    Select color
                  </option>
                  {product.colors.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              ) : <div className="select-placeholder"></div>}
            </div>

            {/* ADD TO CART */}
            <button
              className="add-to-cart-btn"
              style={{ backgroundColor: storeInfo?.primaryColor || "#000" }}
              onClick={() => {
                const size = selectedOptions[product.id]?.size;
                const color = selectedOptions[product.id]?.color;

                if (!size || !color) {
                  alert("Please select size and color before adding to cart.");
                  return;
                }

                addOneToCart(
                  product.id,
                  parseFloat(product.price),
                  size,
                  color,
                  product.style || "",
                  product.images[0] || "",
                  product.images[0] || "",
                  "", // thumId
                  product.images[1] || "",
                  product.rating || 0,
                  product.weight || 0,
                  product.imagePrice || 0,
                  color,
                  product.SalePrice || 0,
                  product.imageSize1 || "",
                  product.imageSize2 || "",
                  product.imageSize3 || "",
                  product.imageSize4 || "",
                  product.uploadedImageUrl || "",
                  "" // thumFile
                );
              }}
            >
              Add to Cart
            </button>

          </div>
        ))}
      </div>
    </div>
  );
}
